// ABSREG: the absence register, for hr / coo / admin.
//
// One list of every holiday, sick leave and WFH declaration, newest first, with filters,
// pagination and a detail view. Mounted in the HR group of both the HR and the COO sidebars.
//
// STYLING follows LeaveCalendar.js, the screen this sits beside: tokens(dark) / TYPE / S / RADIUS
// from theme.js, ScrollableTable / LoadError / Btn / useIsNarrow from ui.js, and the SAME four
// absence accents, imported rather than redefined so a type's colour here can never disagree with
// the calendar's.
//
// NOTES ARE NEVER REQUESTED AND NEVER RENDERED (point 3d). The server does not return them for
// any viewer - see services/absence_register.py - so there is nothing to hide in this file; it is
// stated here because "the register shows everything about an absence" is the natural reading, and
// the free text is precisely the part HRPAYFIX point 6 withheld from every viewer including the
// COO, a sick note being Article 9 data.
//
// MEDCERT: the certificate column shows the STATUS only (not required / required / missing /
// uploaded). The file itself is opened through the EXISTING audited document route, which applies
// its own Article 9 gate server side - this screen never serves a byte of it and never decides who
// may read it. A viewer who may see the status is not thereby allowed the document, so the link is
// offered whenever a certificate exists and a refusal comes from the server, which is the only
// copy of that rule.
//
// WFHCOUNTRY: the country column is populated only for a WFH day worked from another country.
// Blank means the usual place of work, not unknown.
//
// PENDING ROWS LINK TO THE EXISTING APPROVAL SCREEN (point 3e) and this file contains NO approve
// or reject control: `pending_approval` comes from the server, and the link routes to the
// Validation section that already owns that decision. A second approval mechanism is exactly what
// point 3e rules out.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import { TYPE, S, RADIUS, tokens } from './theme';
import { Btn, LoadError, ScrollableTable, useIsNarrow } from './ui';
import { ABSENCE_ACCENT } from './LeaveCalendar';
// WFHLIST point 3: the shared country label and the sorted list, so the register, the bell
// and the declaration form all name a country the same way in the reader's own language.
import { countryLabel, sortedCountries } from './wfhCountryLabel';

const API = process.env.REACT_APP_API_URL || '/api';

const ALL_TYPES = ['holiday', 'sick', 'wfh'];
// Every status the server's register_status() can report, which is the two LeaveDeclaration axes
// resolved into one value. Listed here only to build the filter dropdown; the server remains the
// one that decides a row's status.
const ALL_STATUSES = ['pending', 'approved', 'rejected', 'active', 'cancel_pending', 'cancelled'];

// MEDCERT: the four statuses medical_certificate.py can report. Listed here only to build the
// filter dropdown; the SERVER decides a row's status, and the verify script asserts the two
// lists agree so a value cannot be offered here that the server would reject.
const ALL_CERT_STATUSES = ['not_required', 'required', 'missing', 'uploaded'];

// Only the two states that need attention carry a tint; not_required and uploaded are quiet.
const CERT_TONE = {
  missing:      { fg: '#A63A3A', bg: '#fdf5f5' },
  required:     { fg: '#92400E', bg: '#FEF3C7' },
  uploaded:     { fg: '#1e7e45', bg: '#e9f7ef' },
  not_required: { fg: '#4b5563', bg: '#f1f2f4' },
};

const PAGE_SIZE = 25;

// Status pill colours. Neutral by default so only the states that need attention carry a tint.
const STATUS_TONE = {
  pending:        { fg: '#92400E', bg: '#FEF3C7' },
  approved:       { fg: '#1e7e45', bg: '#e9f7ef' },
  rejected:       { fg: '#A63A3A', bg: '#fdf5f5' },
  cancelled:      { fg: '#4b5563', bg: '#f1f2f4' },
  cancel_pending: { fg: '#92400E', bg: '#FEF3C7' },
  active:         { fg: '#1e5fa8', bg: '#e8f1fb' },
};

export default function AbsenceRegister({ dark = false, onOpenApprovals }) {
  const { t, i18n } = useTranslation();
  const tk = tokens(dark);
  const isNarrow = useIsNarrow();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [entities, setEntities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selected, setSelected] = useState(null);

  // Filters (point 3b). Held as one object so a change can reset the page in one place.
  const [filters, setFilters] = useState({
    leave_type: '', status: '', entity: '', employee: '', date_from: '', date_to: '',
    wfh_country: '', certificate_status: '',
  });

  const setFilter = (key, value) => {
    setFilters((f) => ({ ...f, [key]: value }));
    // A filter change must return to page 1: staying on page 3 of a newly narrowed result set
    // shows an empty table that looks like "no absences" rather than "no page 3".
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ leave_type: '', status: '', entity: '', employee: '', date_from: '',
                 date_to: '', wfh_country: '', certificate_status: '' });
    setPage(1);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (filters.leave_type) params.leave_type = filters.leave_type;
      if (filters.status) params.status = filters.status;
      if (filters.entity) params.entity = filters.entity;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.wfh_country) params.wfh_country = filters.wfh_country;
      if (filters.certificate_status) params.certificate_status = filters.certificate_status;
      const res = await axios.get(`${API}/absence-register/`, { params });
      setRows(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/absence-register/entities`)
      .then((r) => { if (!cancelled) setEntities(r.data?.entities || []); })
      .catch(() => { /* the filter simply offers no entity options */ });
    return () => { cancelled = true; };
  }, []);

  // WFHCOUNTRY: the permitted list comes from the SERVER, never a copy in this file, so the
  // filter can only offer countries the validator would accept.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/absence-register/wfh-countries`)
      .then((r) => { if (!cancelled) setCountries(r.data?.countries || []); })
      .catch(() => { /* the filter simply offers no country options */ });
    return () => { cancelled = true; };
  }, []);

  // The employee filter is applied in the browser against the loaded page, because the server
  // filters by employee_id and this box is a free-text NAME search. Stated rather than hidden:
  // it narrows the current page, which is why it sits beside the name column.
  const visibleRows = useMemo(() => {
    const q = (filters.employee || '').trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (r.employee_name || '').toLowerCase().includes(q));
  }, [rows, filters.employee]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const s = {
    wrap: { display: 'flex', flexDirection: 'column', gap: S[4] },
    filterBar: {
      display: 'flex', flexWrap: 'wrap', gap: S[3], alignItems: 'flex-end',
      padding: S[3], borderRadius: RADIUS.md,
      backgroundColor: tk.sf2, border: `1px solid ${tk.bd}`,
    },
    field: { display: 'flex', flexDirection: 'column', gap: S[1], minWidth: isNarrow ? '100%' : '150px' },
    label: { ...TYPE.label, color: tk.tx3 },
    input: {
      ...TYPE.body, padding: '7px 9px', borderRadius: RADIUS.sm,
      border: `1px solid ${tk.bd}`, backgroundColor: tk.sf, color: tk.tx, outline: 'none',
    },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: {
      ...TYPE.label, color: tk.tx3, textAlign: 'left', padding: S[2],
      borderBottom: `1px solid ${tk.bd}`, whiteSpace: 'nowrap',
    },
    td: { ...TYPE.body, color: tk.tx, padding: S[2], borderBottom: `1px solid ${tk.bd2 || tk.bd}` },
    row: { cursor: 'pointer' },
    pill: (tone) => ({
      ...TYPE.small, display: 'inline-block', padding: '2px 8px',
      borderRadius: RADIUS.pill, color: tone.fg, backgroundColor: tone.bg, whiteSpace: 'nowrap',
    }),
    empty: { ...TYPE.body, color: tk.tx3, padding: S[5], textAlign: 'center' },
    pager: { display: 'flex', alignItems: 'center', gap: S[3], justifyContent: 'flex-end' },
    detail: {
      display: 'flex', flexDirection: 'column', gap: S[3],
      padding: S[4], borderRadius: RADIUS.md,
      backgroundColor: tk.sf2, border: `1px solid ${tk.bd}`,
    },
    detailGrid: {
      display: 'grid', gridTemplateColumns: isNarrow ? '1fr' : 'repeat(3, minmax(0, 1fr))',
      gap: S[3],
    },
  };

  const typeLabel = (v) => t(`absenceRegister.type.${v}`, v);
  const statusLabel = (v) => t(`absenceRegister.status.${v}`, v);

  const renderStatus = (value) => {
    const tone = STATUS_TONE[value] || STATUS_TONE.active;
    return <span style={s.pill(tone)}>{statusLabel(value)}</span>;
  };

  // MEDCERT: the status pill, plus a link to the FILE when one exists.
  //
  // THE LINK IS OFFERED WHENEVER A CERTIFICATE EXISTS, and the server decides whether this viewer
  // may open it. That is deliberate: who may read Article 9 health data is defined once, in
  // medical_certificate.may_open_certificate, and a second copy of that rule here could drift and
  // would be the copy that is wrong. The document route answers 403 for a viewer who may not,
  // which is the enforcing check; hiding the link as well would mean two rules to keep in step.
  const renderCert = (row) => {
    const tone = CERT_TONE[row.certificate_status] || CERT_TONE.not_required;
    const pill = (
      <span style={s.pill(tone)}>{t(`absenceRegister.cert.${row.certificate_status}`,
                                   row.certificate_status)}</span>
    );
    if (!row.certificate_document_id) return pill;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: S[2] }}>
        {pill}
        <a
          href={`${API}/documents/${row.certificate_document_id}/view`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ ...TYPE.small, color: tk.ac || '#1e5fa8' }}
          onClick={(e) => e.stopPropagation()}
          title={t('absenceRegister.openCertificateHint')}
        >
          {t('absenceRegister.openCertificate')}
        </a>
      </span>
    );
  };

  const renderType = (value) => {
    const accent = ABSENCE_ACCENT[value] || ABSENCE_ACCENT.absent;
    return <span style={s.pill({ fg: accent.fg, bg: accent.bg })}>{typeLabel(value)}</span>;
  };

  if (error) return <LoadError dark={dark} onRetry={load} />;

  return (
    <div style={s.wrap}>
      {/* ── Filters (point 3b) ── */}
      <div style={s.filterBar}>
        <div style={s.field}>
          <label style={s.label} htmlFor="absreg-type">{t('absenceRegister.filterType')}</label>
          <select id="absreg-type" style={s.input} value={filters.leave_type}
                  onChange={(e) => setFilter('leave_type', e.target.value)}>
            <option value="">{t('absenceRegister.allTypes')}</option>
            {ALL_TYPES.map((v) => <option key={v} value={v}>{typeLabel(v)}</option>)}
          </select>
        </div>
        <div style={s.field}>
          <label style={s.label} htmlFor="absreg-status">{t('absenceRegister.filterStatus')}</label>
          <select id="absreg-status" style={s.input} value={filters.status}
                  onChange={(e) => setFilter('status', e.target.value)}>
            <option value="">{t('absenceRegister.allStatuses')}</option>
            {ALL_STATUSES.map((v) => <option key={v} value={v}>{statusLabel(v)}</option>)}
          </select>
        </div>
        <div style={s.field}>
          <label style={s.label} htmlFor="absreg-entity">{t('absenceRegister.filterEntity')}</label>
          <select id="absreg-entity" style={s.input} value={filters.entity}
                  onChange={(e) => setFilter('entity', e.target.value)}>
            <option value="">{t('absenceRegister.allEntities')}</option>
            {entities.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div style={s.field}>
          <label style={s.label} htmlFor="absreg-employee">{t('absenceRegister.filterEmployee')}</label>
          <input id="absreg-employee" style={s.input} value={filters.employee}
                 placeholder={t('absenceRegister.employeePlaceholder')}
                 onChange={(e) => setFilter('employee', e.target.value)} />
        </div>
        <div style={s.field}>
          <label style={s.label} htmlFor="absreg-from">{t('absenceRegister.filterFrom')}</label>
          <input id="absreg-from" type="date" style={s.input} value={filters.date_from}
                 onChange={(e) => setFilter('date_from', e.target.value)} />
        </div>
        <div style={s.field}>
          <label style={s.label} htmlFor="absreg-to">{t('absenceRegister.filterTo')}</label>
          <input id="absreg-to" type="date" style={s.input} value={filters.date_to}
                 onChange={(e) => setFilter('date_to', e.target.value)} />
        </div>
        <div style={s.field}>
          <label style={s.label} htmlFor="absreg-country">{t('absenceRegister.filterCountry')}</label>
          <select id="absreg-country" style={s.input} value={filters.wfh_country}
                  onChange={(e) => setFilter('wfh_country', e.target.value)}>
            <option value="">{t('absenceRegister.allCountries')}</option>
            {/* WFHLIST: the OPTION VALUE stays the stored country, because that is what the
                server filters on; only the visible text is translated and sorted. */}
            {sortedCountries(t, countries, i18n && i18n.language)
              .map((o) => <option key={o.stored} value={o.stored}>{o.label}</option>)}
          </select>
        </div>
        <div style={s.field}>
          <label style={s.label} htmlFor="absreg-cert">{t('absenceRegister.filterCertificate')}</label>
          <select id="absreg-cert" style={s.input} value={filters.certificate_status}
                  onChange={(e) => setFilter('certificate_status', e.target.value)}>
            <option value="">{t('absenceRegister.allCertificates')}</option>
            {ALL_CERT_STATUSES.map((v) => (
              <option key={v} value={v}>{t(`absenceRegister.cert.${v}`, v)}</option>
            ))}
          </select>
        </div>
        <Btn variant="secondary" dark={dark} onClick={clearFilters}>
          {t('absenceRegister.clearFilters')}
        </Btn>
      </div>

      {/* ── Detail (point 3c) ── */}
      {selected && (
        <div style={s.detail}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        gap: S[3], flexWrap: 'wrap' }}>
            <strong style={{ ...TYPE.h3, color: tk.tx }}>
              {selected.employee_name || t('absenceRegister.unknownEmployee')}
            </strong>
            <Btn variant="secondary" dark={dark} onClick={() => setSelected(null)}>
              {t('absenceRegister.closeDetail')}
            </Btn>
          </div>
          <div style={s.detailGrid}>
            <Detail tk={tk} label={t('absenceRegister.colType')}>{renderType(selected.leave_type)}</Detail>
            <Detail tk={tk} label={t('absenceRegister.colStatus')}>{renderStatus(selected.status)}</Detail>
            <Detail tk={tk} label={t('absenceRegister.colEntity')}>{selected.entity || '-'}</Detail>
            <Detail tk={tk} label={t('absenceRegister.colStart')}>{selected.start_date || '-'}</Detail>
            <Detail tk={tk} label={t('absenceRegister.colEnd')}>{selected.end_date || '-'}</Detail>
            <Detail tk={tk} label={t('absenceRegister.colDays')}>{selected.days}</Detail>
            <Detail tk={tk} label={t('absenceRegister.colCountry')}>
              {/* WFHLIST: translated, with the stored value as the fallback so a declaration
                  made before a list change still shows the country it actually holds. */}
              {countryLabel(t, selected.wfh_country) || '-'}
            </Detail>
            <Detail tk={tk} label={t('absenceRegister.colCertificate')}>
              {renderCert(selected)}
            </Detail>
            {selected.certificate_due_date && (
              <Detail tk={tk} label={t('absenceRegister.certificateDue')}>
                {selected.certificate_due_date}
              </Detail>
            )}
          </div>
          {/* Point 3e: a pending request links to the EXISTING approval screen. No approve or
              reject control is rendered here, by design. */}
          {selected.pending_approval && (
            <div>
              <Btn dark={dark} onClick={() => onOpenApprovals && onOpenApprovals(selected)}>
                {t('absenceRegister.openApproval')}
              </Btn>
            </div>
          )}
          {/* Stated in the UI, not only in a comment: the reader should know the free text is
              withheld by policy rather than wonder whether the register is incomplete. */}
          <p style={{ ...TYPE.small, color: tk.tx3, margin: 0 }}>
            {t('absenceRegister.notesWithheld')}
          </p>
        </div>
      )}

      {/* ── The list (point 3a) ── */}
      <ScrollableTable>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>{t('absenceRegister.colEmployee')}</th>
              <th style={s.th}>{t('absenceRegister.colEntity')}</th>
              <th style={s.th}>{t('absenceRegister.colType')}</th>
              <th style={s.th}>{t('absenceRegister.colStart')}</th>
              <th style={s.th}>{t('absenceRegister.colEnd')}</th>
              <th style={s.th}>{t('absenceRegister.colDays')}</th>
              <th style={s.th}>{t('absenceRegister.colStatus')}</th>
              <th style={s.th}>{t('absenceRegister.colCountry')}</th>
              <th style={s.th}>{t('absenceRegister.colCertificate')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td style={s.empty} colSpan={9}>{t('absenceRegister.loading')}</td></tr>
            )}
            {!loading && visibleRows.length === 0 && (
              <tr><td style={s.empty} colSpan={9}>{t('absenceRegister.empty')}</td></tr>
            )}
            {!loading && visibleRows.map((r) => (
              <tr key={r.id} style={s.row} onClick={() => setSelected(r)}
                  title={t('absenceRegister.openDetail')}>
                <td style={s.td}>{r.employee_name || t('absenceRegister.unknownEmployee')}</td>
                <td style={s.td}>{r.entity || '-'}</td>
                <td style={s.td}>{renderType(r.leave_type)}</td>
                <td style={s.td}>{r.start_date || '-'}</td>
                <td style={s.td}>{r.end_date || '-'}</td>
                <td style={s.td}>{r.days}</td>
                <td style={s.td}>{renderStatus(r.status)}</td>
                <td style={s.td}>{countryLabel(t, r.wfh_country) || '-'}</td>
                <td style={s.td}>{renderCert(r)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollableTable>

      {/* ── Pagination (point 3b) ── */}
      <div style={s.pager}>
        <span style={{ ...TYPE.small, color: tk.tx3 }}>
          {t('absenceRegister.pageOf', { page, pages: pageCount, total })}
        </span>
        <Btn variant="secondary" dark={dark} disabled={page <= 1}
             onClick={() => setPage((p) => Math.max(1, p - 1))}>
          {t('absenceRegister.prev')}
        </Btn>
        <Btn variant="secondary" dark={dark} disabled={page >= pageCount}
             onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>
          {t('absenceRegister.next')}
        </Btn>
      </div>
    </div>
  );
}

function Detail({ tk, label, children }) {
  return (
    <div>
      <p style={{ ...TYPE.label, color: tk.tx3, margin: `0 0 ${S[1]} 0` }}>{label}</p>
      <div style={{ ...TYPE.body, color: tk.tx }}>{children}</div>
    </div>
  );
}
